import { create } from 'zustand';

import type { Course } from '@/api/entitties';
import { fetchCourseContents, fetchCourseLanding, updateCourseProgress } from '@/api/api';

interface CoursesState {
  courses: Pick<Course, 'id' | 'title' | 'course_card'>[];
  courseDetails: Map<string, Course>;
  error: string | null;
  loadingStates: Map<string, { contents: boolean; landing: boolean }>;
  setCourses: (courses: Pick<Course, 'id' | 'title' | 'course_card'>[]) => void;
  setError: (error: string | null) => void;
  fetchCourseContents: (courseId: string) => Promise<void>;
  fetchCourseLanding: (courseId: string) => Promise<void>;
  getCourse: (courseId: string) => Course | undefined;
  ensureCourseContents: (courseId: string) => Promise<void>;
  ensureCourseLanding: (courseId: string) => Promise<void>;
  isCourseContentsLoading: (courseId: string) => boolean;
  isCourseLandingLoading: (courseId: string) => boolean;
  toggleLectureCompletion: (courseId: string, lectureId: string) => Promise<void>;
}

const getInitialLoadingState = () => ({ contents: false, landing: false });

export const useCoursesStore = create<CoursesState>((set, get) => ({
  courses: [],
  courseDetails: new Map(),
  error: null,
  loadingStates: new Map(),

  setCourses: (courses) => set({ courses }),

  setError: (error) => set({ error }),

  fetchCourseContents: async (courseId: string) => {
    const { courseDetails } = get();
    if (courseDetails.has(courseId)) {
      const course = courseDetails.get(courseId)!;
      if (course.sections) return; // Already have contents
    }

    set((state) => ({
      loadingStates: new Map(state.loadingStates).set(courseId, {
        ...getInitialLoadingState(),
        contents: true,
      }),
      error: null,
    }));

    try {
      const data = await fetchCourseContents(courseId);
      set((state) => ({
        courseDetails: new Map(state.courseDetails).set(courseId, {
          ...state.courseDetails.get(courseId),
          ...data,
        }),
        loadingStates: new Map(state.loadingStates).set(courseId, {
          ...getInitialLoadingState(),
          contents: false,
        }),
      }));
    } catch (error) {
      set((state) => ({
        error: error instanceof Error ? error.message : 'Failed to load course contents',
        loadingStates: new Map(state.loadingStates).set(courseId, {
          ...getInitialLoadingState(),
          contents: false,
        }),
      }));
    }
  },

  fetchCourseLanding: async (courseId: string) => {
    const { courseDetails } = get();
    if (courseDetails.has(courseId)) {
      const course = courseDetails.get(courseId)!;
      if (course.landing_page && course.subscription) return; // Already have landing data
    }

    set((state) => ({
      loadingStates: new Map(state.loadingStates).set(courseId, {
        ...getInitialLoadingState(),
        landing: true,
      }),
      error: null,
    }));

    try {
      const data = await fetchCourseLanding(courseId);
      set((state) => ({
        courseDetails: new Map(state.courseDetails).set(courseId, {
          ...state.courseDetails.get(courseId),
          ...data,
        }),
        loadingStates: new Map(state.loadingStates).set(courseId, {
          ...getInitialLoadingState(),
          landing: false,
        }),
      }));
    } catch (error) {
      set((state) => ({
        error: error instanceof Error ? error.message : 'Failed to load course landing',
        loadingStates: new Map(state.loadingStates).set(courseId, {
          ...getInitialLoadingState(),
          landing: false,
        }),
      }));
    }
  },

  getCourse: (courseId: string) => {
    const { courses, courseDetails } = get();
    const course = courses.find(c => c.id === courseId);
    if (!course) return undefined;

    const details = courseDetails.get(courseId);
    return details ? { ...course, ...details } : course;
  },

  ensureCourseContents: async (courseId: string) => {
    const { courseDetails } = get();
    const course = courseDetails.get(courseId);
    if (course?.sections) return; // Already have contents

    await get().fetchCourseContents(courseId);
  },

  ensureCourseLanding: async (courseId: string) => {
    const { courseDetails } = get();
    const course = courseDetails.get(courseId);
    if (course?.landing_page && course?.subscription) return; // Already have landing data

    await get().fetchCourseLanding(courseId);
  },

  isCourseContentsLoading: (courseId: string) => {
    return get().loadingStates.get(courseId)?.contents ?? false;
  },

  isCourseLandingLoading: (courseId: string) => {
    return get().loadingStates.get(courseId)?.landing ?? false;
  },

  toggleLectureCompletion: async (courseId: string, lectureId: string) => {
    const course = get().courseDetails.get(courseId);
    if (!course) return;

    const doneLectures = course.progress?.doneLectures ?? {};
    const isDone = doneLectures[lectureId];
    if (isDone) {
      delete doneLectures[lectureId];
    } else {
      doneLectures[lectureId] = true;
    }

    // Optimistically update UI
    set((state) => ({
      courseDetails: new Map(state.courseDetails).set(courseId, {
        ...course,
        progress: {
          ...course.progress,
          doneLectures,
        },
      }),
    }));

    // Persist to backend
    try {
      const progressUpdates = isDone
        ? { undoneLectureIds: [lectureId] }
        : { doneLectureIds: [lectureId] };
      await updateCourseProgress(courseId, progressUpdates);
    } catch (error) {
      // Revert on error
      set((state) => ({
        courseDetails: new Map(state.courseDetails).set(courseId, {
          ...course,
          progress: {
            ...course.progress,
            doneLectures: course.progress?.doneLectures ?? {},
          },
        }),
      }));
      console.error('Failed to update lecture completion status:', error);
    }
  },
}));